<template>
  <Teleport to="body">
    <div v-if="showWarning" class="licence-warning-overlay" :key="overlayKey">
      <div class="licence-warning-modal">
        <!-- Header -->
        <div class="modal-header border-0 pb-0">
          <div class="d-flex align-items-center">
            <div class="warning-icon-container me-3">
              <i class="fas fa-exclamation-triangle text-warning fs-2"></i>
            </div>
            <div>
              <h4 class="modal-title mb-1 text-warning fw-bold">{{ $t('payment_notification') }}</h4>
              <small class="text-muted">{{ $t('immediate_action_required') }}</small>
            </div>
          </div>
          <button 
            v-if="canClose" 
            @click="closeWarning" 
            type="button" 
            class="btn btn-icon btn-sm btn-active-icon-primary"
            :aria-label="$t('close')"
          >
          <i class="bi bi-x fs-1"></i>
          </button>
        </div>
        
        <!-- Body -->
        <div class="modal-body pt-3">
          <!-- Ana Uyarı Mesajı -->
          <div class="alert alert-warning text-gray-800 border-0 mb-3" role="alert">
            <div class="d-flex">
              <div class="flex-grow-1">
                <p class="mb-2 fw-medium">{{ warningMessage }}</p>
                <div v-if="userData.payment_delay_days > 0" class="small text-gray-800">
                  {{ $t('delay_period_userdata_payment_delay_days', [userData.payment_delay_days]) }}
                </div>
              </div>
            </div>
          </div>

          <!-- Critical Notice -->
          <div v-if="!canClose" class="alert alert-danger border-0 mb-3" role="alert">
            <div class="d-flex align-items-start">
              <i class="fas fa-lock text-danger me-2 mt-1"></i>
              <div>
                <strong>{{ $t('access_restricted') }}</strong>
                <p class="mb-0 small mt-1">{{ $t('this_warning_cannot_be_closed_due_to_delay_of_more') }}</p>
              </div>
            </div>
          </div>
        </div>
        
        <!-- Footer -->
        <div class="modal-footer border-0 pt-0">
          <div class="d-grid gap-2 w-100">
            <!-- Ana Ödeme Butonu -->
            <button 
              @click="goToPayment" 
              :disabled="isProcessingPayment"
              class="btn btn-primary btn-lg d-flex align-items-center justify-content-center"
              type="button"
            >
              <div v-if="isProcessingPayment" class="spinner-border spinner-border-sm me-2" role="status">
                <span class="visually-hidden">{{ $t('loading') }}</span>
              </div>
              <i v-else class="fas fa-credit-card me-2"></i>
              <span>{{ isProcessingPayment ? $t('processing') : $t('retry_payment') }}</span>
            </button>
            
            <!-- İkincil Aksiyonlar -->
            <div class="row g-2">
              <div class="col">
                <button 
                  v-if="canClose" 
                  @click="closeWarning" 
                  class="btn btn-secondary w-100"
                  type="button"
                >
                  <i class="fas fa-clock me-1"></i>
                  {{ $t('remind_later') }}
                </button>
              </div>
              <div class="col">
                <button 
                  @click="performSignOut()" 
                  class="btn btn-danger w-100"
                  type="button"
                >
                  <i class="fas fa-sign-out-alt me-1"></i>
                  {{ $t('sign_out') }}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<script>
import axios from 'axios';

export default {
  name: 'LicenceWarning',  
  data() {
    return {
      userData: this.$getUser() || {},
      isWarningDismissed: false,
      overlayKey: Date.now(),
      isProcessingPayment: false,
      routeChangeCount: 0
    }
  },
  
  computed: {
    showWarning() {
      return this.userData.is_payment_delayed === 1 && !this.isWarningDismissed;
    },
    
    canClose() {
      return this.userData.payment_delay_days <= 3;
    },
    
    warningMessage() {
      const delayDays = this.userData.payment_delay_days;
      
      if (delayDays === 0) {
        return this.$t('your_payment_could_not_be_processed_please_check_payment');
      } else if (delayDays <= 3) {
        return this.$t('your_payment_is_delaydays_days_overdue_please_make_payment', [delayDays]);
      } else {
        return this.$t('your_payment_is_delaydays_days_overdue_and_restricted', [delayDays]);
      }
    }
  },
  
  methods: {
    handlePaymentStatusUpdate(event) {
      this.userData = event.detail;
      
      // Eğer yeni bir payment delay varsa warning'i resetle
      if (event.detail.is_payment_delayed === 1) {
        this.isWarningDismissed = false;
        this.overlayKey = Date.now();
      }
    },

    closeWarning() {
      if (this.canClose) {
        this.isWarningDismissed = true;
        this.$emit('warning-dismissed');
      }
    },
    
    async retryStripePayment() {
      try {
        this.isProcessingPayment = true;
        
        const requestData = {
          session_id: this.$getUser().session_id,
          email: this.$getUser().user_mail
        };

        const response = await axios.post('/api/Payment/RetrySubscription', requestData, {
          headers: {
            'Content-Type': 'application/json'
          },
          timeout: 30000 // 30 saniye timeout
        });

        if (response?.data?.success) {
          if (response.data.checkoutUrl) {
            this.$swal({
              title: this.$t('success'),
              text: this.$t('redirecting_to_payment_page'),
              icon: 'success',
              timer: 2000,
              showConfirmButton: false
            });
            
            // 1 saniye bekle sonra yönlendir
            setTimeout(() => {
              window.location.href = response.data.checkoutUrl;
            }, 1000);
          } 
          else if (response.data.clientSecret) {
            await this.handleStripePaymentIntent(response.data.clientSecret);
          }
          else {
            throw new Error('Payment URL could not be retrieved');
          }
        } else {
          throw new Error(response.data.message || this.$t('payment_retry_failed'));
        }

      } catch (error) {
        console.error('Stripe payment retry error:', error);
        
        let errorMessage = this.$t('an_error_occured');
        if (error.response?.data?.message) {
          errorMessage = error.response.data.message;
        } else if (error.message) {
          errorMessage = error.message;
        }
        
        this.$swal({
          title: this.$t('payment_failed'),
          text: errorMessage,
          icon: 'error',
          confirmButtonText: this.$t('ok'),
          confirmButtonColor: '#dc3545'
        });
      } finally {
        this.isProcessingPayment = false;
      }
    },

    async handleStripePaymentIntent(clientSecret) {
      try {
        if (!window.Stripe) {
          throw new Error('Stripe.js is not loaded');
        }
        
        let strpk = this.$getUser().stripe ? this.$getUser().stripe.strpk : null;
        const stripe = window.Stripe(strpk);
        
        const {error} = await stripe.confirmPayment({
          clientSecret: clientSecret,
          confirmParams: {
            return_url: `${window.location.origin}/payment-success`
          }
        });

        if (error) {
          throw new Error(error.message);
        }

        // Başarılı ödeme sonrası
        this.$swal({
          title: this.$t('success'),
          text: this.$t('your_payment_has_been_processed'),
          icon: 'success',
          confirmButtonText: this.$t('ok')
        });
        
      } catch (error) {
        console.error('Stripe PaymentIntent error:', error);
        throw error;
      }
    },

    goToPayment() {
      this.retryStripePayment();
    },

    resetWarning() {
      if (this.userData.is_payment_delayed === 1) {
        // Critical warnings (3+ days) never close
        if (!this.canClose) {
          this.isWarningDismissed = false;
        } 
        // Normal warnings only show again after 3 route changes
        else if (this.routeChangeCount >= 3) {
          this.isWarningDismissed = false;
          this.routeChangeCount = 0;
        }
        
        // Fresh render with new overlay key
        this.overlayKey = Date.now();
      }
    },

    async performSignOut() {
      try {
        const parameters = {
          session_id: this.$getUser().session_id
        };  
        
        await axios.post('/api/Auth/Logout', parameters, {
          'Content-Type': 'application/json'
        });

        this.$removeUser('user_' + this.$getUser().session_id);
        
        if (this.$getSessionStorage('is_instant_login')) {
          sessionStorage.clear();
          window.close();
        } else { 
          sessionStorage.clear();
          this.$router.push({ name: 'Login' });
        }
      } catch (error) {
        console.error('Logout error:', error);
        // Hata durumunda da çıkışı tamamla
        sessionStorage.clear();
        this.$router.push({ name: 'Login' });
      }
    },

    formatDate(dateString) {
      if (!dateString) return this.$t('unknown');
      try {
        return new Date(dateString).toLocaleDateString();
      } catch {
        return this.$t('unknown');
      }
    }
  },

  watch: {
    '$route'() {
      this.routeChangeCount = (this.routeChangeCount || 0) + 1;      
      this.userData = this.$getUser() || {};
      this.resetWarning();
    },
    
    'userData.is_payment_delayed': {
      handler(newVal) {
        if (newVal === 1) {
          this.isWarningDismissed = false;
          this.overlayKey = Date.now();
        }
      },
      immediate: true
    }
  },

  created() {
    this.routeChangeCount = 0;
    window.addEventListener('payment-status-updated', this.handlePaymentStatusUpdate);
  },

  beforeUnmount() {
    window.removeEventListener('payment-status-updated', this.handlePaymentStatusUpdate);
  }
}
</script>

<style scoped>
.licence-warning-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.6);
  backdrop-filter: blur(8px);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 99999;
  animation: fadeInBackdrop 0.4s cubic-bezier(0.4, 0, 0.2, 1);
}

.licence-warning-modal {
  background: var(--bs-body-bg);
  border-radius: 20px;
  box-shadow: 
    0 25px 50px -12px rgba(0, 0, 0, 0.25),
    0 0 0 1px rgba(108, 117, 125, 0.2),
    inset 0 1px 0 rgba(108, 117, 125, 0.1);
  max-width: 560px;
  width: 90%;
  max-height: 90vh;
  overflow: hidden;
  position: relative;
  animation: slideInScale 0.4s cubic-bezier(0.34, 1.56, 0.64, 1);
}

/* Gradient Background */
.licence-warning-modal::before {
  content: "";
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 120px;
  background: linear-gradient(135deg, rgba(255, 193, 7, 0.1) 0%, rgba(255, 152, 0, 0.05) 100%);
  border-radius: 20px 20px 0 0;
  z-index: -1;
}

/* Header Styling */
.modal-header {
  padding: 2rem 2rem 1rem;
  position: relative;
  background: transparent;
}

.warning-icon-container {
  width: 70px;
  height: 70px;
  background: linear-gradient(135deg, rgba(255, 193, 7, 0.15), rgba(255, 152, 0, 0.15));
  border: 2px solid rgba(255, 193, 7, 0.3);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  position: relative;
  overflow: hidden;
}

.warning-icon-container i {
  font-size: 2rem !important;
  color: #ffc107;
  animation: pulseGlow 2s infinite;
}

.modal-title {
  color: #ffc107;
  font-size: 1.5rem;
  font-weight: 700;
  margin-bottom: 0.5rem;
}

.modal-header small {
  color: var(--bs-secondary-color);
  font-weight: 500;
  font-size: 0.875rem;
}

/* Close Button */
.btn-close {
  position: absolute;
  top: 1.5rem;
  right: 1.5rem;
  padding: 0.5rem;
  opacity: 0.6;
  transition: all 0.3s ease;
  border-radius: 50%;
  background: var(--bs-secondary-bg);
}

.btn-close:hover {
  opacity: 1;
  transform: scale(1.1);
  background: var(--bs-danger-bg-subtle);
}

/* Body Styling */
.modal-body {
  padding: 1rem 2rem 1.5rem;
}

.alert {
  border-radius: 16px;
  border: none !important;
  position: relative;
  overflow: hidden;
}

.alert-warning {
  background: rgba(255, 193, 7, 0.1);
  color: var(--bs-body-color);
  border-left: 4px solid #ffc107;
  box-shadow: 0 4px 15px rgba(255, 193, 7, 0.2);
}

.alert-danger {
  background: rgba(220, 53, 69, 0.1);
  color: var(--bs-body-color);
  border-left: 4px solid #dc3545;
  box-shadow: 0 4px 15px rgba(220, 53, 69, 0.2);
}

/* Footer Styling */
.modal-footer {
  padding: 1.5rem 2rem 2rem;
  background: transparent;
}

/* Loading Spinner */
.spinner-border-sm {
  width: 1.25rem;
  height: 1.25rem;
}

/* Animations */
@keyframes fadeInBackdrop {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

@keyframes slideInScale {
  from {
    opacity: 0;
    transform: scale(0.9) translateY(20px);
  }
  to {
    opacity: 1;
    transform: scale(1) translateY(0);
  }
}

@keyframes pulseGlow {
  0%, 100% {
    filter: drop-shadow(0 0 8px rgba(255, 193, 7, 0.5));
    transform: scale(1);
  }
  50% {
    filter: drop-shadow(0 0 16px rgba(255, 193, 7, 0.8));
    transform: scale(1.05);
  }
}

/* Responsive Design */
@media (max-width: 576px) {
  .licence-warning-modal {
    margin: 1rem;
    width: calc(100% - 2rem);
    max-height: calc(100vh - 2rem);
    border-radius: 16px;
  }
  
  .modal-header,
  .modal-body,
  .modal-footer {
    padding-left: 1.5rem;
    padding-right: 1.5rem;
  }
  
  .warning-icon-container {
    width: 60px;
    height: 60px;
  }
  
  .warning-icon-container i {
    font-size: 1.5rem !important;
  }
  
  .modal-title {
    font-size: 1.25rem;
  }
  
  .btn-lg {
    padding: 0.75rem 1.5rem;
    font-size: 1rem;
    min-height: 3rem;
  }
}

/* Focus States for Accessibility */
.btn-outline-secondary:focus {
  outline-color: var(--bs-secondary);
}

.btn-outline-danger:focus {
  outline-color: var(--bs-danger);
}

/* Improved Text Readability */
.alert p {
  line-height: 1.6;
  margin-bottom: 0.5rem;
}

.alert .small {
  line-height: 1.5;
  opacity: 0.8;
}

/* Glass Effect Enhancement */
.licence-warning-modal {
  backdrop-filter: blur(20px);
  background: var(--bs-body-bg);
}

/* Loading state */
.btn:disabled {
  cursor: not-allowed;
}
</style>